import { Input, InputRef, Modal, Popconfirm, Space, Table, Typography } from 'antd'
import { useRef, useState } from 'react'
import { WorksheetListItem } from './BudgetCalculatorApi'
import { createDefaultWorksheet, Worksheet } from './budgetCalculatorModel'
import Import from './Import'
import { faClone, faPlus, faTrash, faSparkles } from '@fortawesome/pro-solid-svg-icons'
import IconAndTextButton from '../IconAndTextButton'
import dayjs from 'dayjs'
import BudgetBuilderModal from './BudgetBuilder/BudgetBuilderModal'

const Link = Typography.Link

export type Props = {
  worksheets: WorksheetListItem[]
  selectWorksheet: (worksheetId: string) => void
  createWorksheet: (worksheet: Worksheet) => void
  copyWorksheet: (worksheetId: string, title: string) => void
  deleteWorksheet: (worksheetId: string) => void
}

const WorksheetList = ({ worksheets, selectWorksheet, createWorksheet, copyWorksheet, deleteWorksheet }: Props) => {
  const [copyModalVisible, setCopyModalVisible] = useState(false)
  const [builderModalVisible, setBuilderModalVisible] = useState(false)
  const [recordToCopy, setRecordToCopy] = useState<WorksheetListItem>({ title: '', budgetWorksheetId: '', isApplied: false, appliedYear: 0, appliedAt: null, updatedAt: null })
  const [titleOfNewCopy, setTitleOfNewCopy] = useState('')
  const modalInputRef = useRef<InputRef>(null);

  const visibleTitle = (title: string) => title?.trim() || '[Unnamed Worksheet]'
  const showCopyModal = (worksheet: WorksheetListItem) => {
    setRecordToCopy(worksheet)
    setTitleOfNewCopy(`Copy of ${worksheet.title}`)
    setCopyModalVisible(true)
    setTimeout(() => modalInputRef.current?.focus({ cursor: 'all' }))
  }
  const makeCopy = () => {
    copyWorksheet(recordToCopy!.budgetWorksheetId, titleOfNewCopy)
    setCopyModalVisible(false)
  }

  return <Space direction="vertical">
    <Space>
      <IconAndTextButton
        onClick={() => setBuilderModalVisible(true)}
        icon={faSparkles}
        text="Create With Budget Builder"
        type="primary"
        style={{ backgroundColor: '#ff6b4a', borderColor: '#ff6b4a' }}
      />
      <IconAndTextButton onClick={() => createWorksheet(createDefaultWorksheet())} icon={faPlus} text="New Worksheet"/>
      <Import createWorksheet={createWorksheet} />
    </Space>

    <BudgetBuilderModal
      open={builderModalVisible}
      onCancel={() => setBuilderModalVisible(false)}
      createWorksheet={createWorksheet}
    />

    <Modal open={copyModalVisible}
      title="Copy Worksheet"
      onCancel={() => setCopyModalVisible(false)}
      onOk={makeCopy}
    >
      <Space direction="vertical">
        <span>Make a copy of <em>{visibleTitle(recordToCopy!.title)}</em> with the name:</span>
        <Input value={titleOfNewCopy}
          onChange={e => setTitleOfNewCopy(e.target.value)}
          onPressEnter={makeCopy}
          ref={modalInputRef}
        />
      </Space>
    </Modal>

    <Table dataSource={worksheets}
      rowKey="budgetWorksheetId"
      columns={[
        {
          dataIndex: 'title',
          title: 'Name',
          render: (title, worksheet) => <Link onClick={() => selectWorksheet(worksheet.budgetWorksheetId)}>{visibleTitle(title)}</Link>
        },
        {
          dataIndex: 'updatedAt',
          title: 'Last Updated',
          render: updatedAt => dayjs(updatedAt).format('MMM D, YYYY')
        },
        {
          dataIndex: 'appliedYear',
          title: 'Applied Year',
          render: (appliedYear, worksheet) => worksheet.isApplied && appliedYear > 0 ? appliedYear : ''
        },
        {
          dataIndex: 'appliedAt',
          title: 'Applied Date',
          render: (appliedAt, worksheet) => worksheet.isApplied ? dayjs(appliedAt).format('MMM D, YYYY') : ''
        },
        {
          title: '',
          render: (_, record) => {
            return <Space>
              <IconAndTextButton onClick={() => showCopyModal(record)} icon={faClone} text="Copy" />
              <Popconfirm
                title="Delete worksheet?"
                description={<span>Are you sure you want to delete <em>{visibleTitle(record.title)}</em>?</span>}
                onConfirm={() => deleteWorksheet(record.budgetWorksheetId)}
                okText="Delete"
                okButtonProps={{ danger: true }}
                placement="topRight"
              >
                <IconAndTextButton danger icon={faTrash} text="Delete"/>
              </Popconfirm>
            </Space>
          }
        }
      ]}
    />
  </Space>
}
export default WorksheetList
